import type { CashReceiptDetail } from '@erp/rpc';
import { T, useGT } from 'gt-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNavigate } from '@tanstack/react-router';

function formatAmount(amount: string | null): string {
  if (amount === null) return '—';
  const num = parseFloat(amount);
  if (isNaN(num)) return amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(dateStr));
}

interface CashReceiptPaymentSectionProps {
  receipt: CashReceiptDetail;
}

export function CashReceiptPaymentSection({
  receipt,
}: CashReceiptPaymentSectionProps) {
  const navigate = useNavigate();
  const gt = useGT();
  const { linkedPayment } = receipt;

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <T context="AR cash receipt, section heading for matched payment">
          Payment Match
        </T>
      </p>

      {linkedPayment ? (
        <div className="rounded-md border bg-muted/30 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {linkedPayment.referenceNumber ??
                gt('(No reference)', {
                  context:
                    'AR payment reference placeholder when reference number is missing',
                })}
            </span>
            <Badge variant="secondary" className="text-xs">
              {linkedPayment.status}
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>
              <T context="AR payment detail label">Amount</T>
            </span>
            <span className="font-medium text-foreground">
              {formatAmount(linkedPayment.paymentAmount)}
            </span>
            <span>
              <T context="AR payment detail label">Date</T>
            </span>
            <span className="font-medium text-foreground">
              {formatDate(linkedPayment.paymentDate)}
            </span>
            {linkedPayment.originSystem && (
              <>
                <span>
                  <T context="AR payment detail label for origin system">
                    Source
                  </T>
                </span>
                <span className="font-medium text-foreground">
                  {linkedPayment.originSystem}
                </span>
              </>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => {
              navigate({
                to: '/accounts-receivable/payments/$arPaymentId',
                params: { arPaymentId: linkedPayment.id },
                viewTransition: true,
              });
            }}
          >
            <T context="AR cash receipt, navigate to linked payment">
              View Payment
            </T>
          </Button>
        </div>
      ) : (
        <div className="rounded-md border border-dashed p-4 text-center">
          <p className="text-sm font-medium text-muted-foreground">
            <T context="AR cash receipt when no payment is matched">
              No match found
            </T>
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            <T context="AR cash receipt empty state explanation">
              This receipt has not been matched to a pending payment.
            </T>
          </p>
        </div>
      )}
    </div>
  );
}
